import { useMemo } from 'react'
import { Box, Divider, Stack, StackProps, Typography } from '@mui/material'
import { KojinResult } from "interface/kojin"

import {
	Person as MatchIcon,
	PersonAdd as MultiHitIcon,
	Error as MayBeIcon,
	Help as UnableIcon,
	Clear as NotFoundIcon
} from '@mui/icons-material'

import { matchingScoreToState } from '../types'
import { SenmoniText } from 'features/kojin/components/senmoniText'

type KojinMatchingCandidateProps = {
	kojin: KojinResult
	isMultiHit: boolean
} & StackProps
export const KojinMatchingCandidate = ({
	kojin,
	isMultiHit,
	...others
}: KojinMatchingCandidateProps) => {
	const state = useMemo(
		() => matchingScoreToState(kojin.nameMatchScore, isMultiHit),
		[kojin.nameMatchScore, isMultiHit]
	)
	const { textColor, icon } = useMemo(
		() => {
			if (state === "match") return { textColor: "primary", icon: <MatchIcon color="success"/> }
			if (state === "multiHit") return { textColor: "default", icon: <MultiHitIcon color="secondary"/> }
			if (state === "maybe") return { textColor: "default", icon: <MayBeIcon color="warning"/> }
			if (state === "unable") return { textColor: "default", icon: <UnableIcon color="error"/> }
			return { textColor: "disabled", icon: <NotFoundIcon color="disabled"/> }
		},
		[state]
	)
	return (
		<Stack
			alignItems="flex-start"
			direction="row"
			spacing={1}
			justifyContent="flex-start"
			overflow="hidden"
			{...others}
		>
			{icon}
			<Stack
				direction="column"
				justifyContent="flex-start"
				overflow="hidden"
				flexGrow={1}
			>
				{isMultiHit && state !== "multiHit" && (
					<Box position='absolute' top={0} left={0} padding={1}>
						<Typography variant="body2" color="error">*</Typography>
					</Box>
				)}
				<Stack direction="row" spacing={1} alignItems="flex-start">
					<Stack flexGrow={1} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
						<Typography key={`isiname`} variant="body1" color={textColor}>{kojin.isiName}</Typography>
						{[
							kojin.isiNameKana ? `(${kojin.isiNameKana})` : null,
							kojin.nenrei ? `${kojin?.nenrei}歳` : null,
							kojin.seibetuCode === '1' ? '男性' : '女性',
							kojin.sotukouName
						].flatMap((word, i) => (
							<Typography key={`primary-${i}`} variant="body2" color={textColor}>{word}</Typography>
						))}
						{kojin.sinryoka?.map((sinryoka, i) => sinryoka.sinryokaName ? <Typography key={`sinryoka-${i}`} variant="body2" color={textColor}>{sinryoka.sinryokaName}</Typography> : <></>)}
						{kojin.senmoni?.map((senmoni, i) => <SenmoniText key={`senmoni-${i}`} color={textColor} senmoni={senmoni}/>)}
					</Stack>
					<Typography variant="caption" color={textColor}>{kojin.kojinCode}</Typography>
				</Stack>

				{(kojin?.organ?.length ?? 0) > 0 ? (
					<>
						<Divider sx={{ marginY: 0.5 }}/>

						<Box>
							{kojin?.organ?.map((organ, i) => (
								<Stack key={`organ-${i}-${organ.sisetuCode}`} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
									{[
										organ.sisetuName,
										organ.bukaName,
										organ.yakusyokuName
									].flatMap((word, j) => (
										<Typography key={`organ-${i}-${organ.sisetuCode}-${j}`} variant="body2" color={textColor}>{word}</Typography>
									))}
								</Stack>
							))}
						</Box>
					</>
				) : <></>}
			</Stack>
		</Stack>
	)
}
